import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Howl } from 'howler';
import './LatestRelease.css';

const LatestRelease = () => {
  const [latestRelease, setLatestRelease] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sound, setSound] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate API call to get latest release
    const fetchLatestRelease = async () => {
      try {
        // This would be replaced with actual API call
        const mockRelease = {
          id: 1,
          title: "NEON PROTOCOL",
          artist: "CYPHER",
          type: "Single",
          cover: "/api/placeholder/400/400",
          releaseDate: "2024-12-15",
          duration: "4:23",
          genre: "Synthwave",
          description: "A cyberpunk anthem that merges industrial beats with ethereal melodies, exploring the boundary between human consciousness and digital reality.",
          audioUrl: "/api/placeholder/audio/track1.mp3",
          streamingLinks: [
            { platform: "Spotify", url: "#", icon: "spotify" },
            { platform: "Apple Music", url: "#", icon: "apple" },
            { platform: "SoundCloud", url: "#", icon: "soundcloud" },
            { platform: "Bandcamp", url: "#", icon: "bandcamp" }
          ]
        };
        
        setLatestRelease(mockRelease);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch latest release:', error);
        setLoading(false);
      }
    };

    fetchLatestRelease();
  }, []);

  const togglePlay = () => {
    if (!latestRelease?.audioUrl) return;

    if (sound) {
      if (isPlaying) {
        sound.pause();
        setIsPlaying(false);
      } else {
        sound.play();
        setIsPlaying(true);
      }
    } else {
      const newSound = new Howl({
        src: [latestRelease.audioUrl],
        html5: true,
        onplay: () => setIsPlaying(true),
        onpause: () => setIsPlaying(false),
        onend: () => setIsPlaying(false),
        onerror: () => {
          console.error('Audio failed to load');
          setIsPlaying(false);
        }
      });
      
      setSound(newSound);
      newSound.play();
    }
  };

  useEffect(() => {
    return () => {
      if (sound) {
        sound.unload();
      }
    };
  }, [sound]);

  if (loading) {
    return (
      <section className="latest-release latest-release--loading">
        <div className="latest-release__loader">
          <div className="latest-release__spinner"></div>
          <p>LOADING LATEST RELEASE...</p>
        </div>
      </section>
    );
  }

  if (!latestRelease) {
    return null;
  }

  return (
    <motion.section 
      className="latest-release"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <div className="latest-release__container">
        <motion.div 
          className="latest-release__header"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h2 className="latest-release__title">
            <span className="latest-release__prefix">LATEST_</span>
            RELEASE
          </h2>
          <div className="latest-release__status">
            <span className="latest-release__status-dot"></span>
            NEW SIGNAL DETECTED
          </div>
        </motion.div>

        <div className="latest-release__content">
          <motion.div 
            className="latest-release__artwork"
            initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="latest-release__cover-container">
              <img 
                src={latestRelease.cover} 
                alt={latestRelease.title}
                className="latest-release__cover"
                onError={(e) => {
                  e.target.src = '/api/placeholder/400/400';
                }}
              />
              <div className="latest-release__cover-overlay">
                <button 
                  className={`latest-release__play-btn ${isPlaying ? 'latest-release__play-btn--playing' : ''}`}
                  onClick={togglePlay}
                >
                  <div className="latest-release__play-icon">
                    {isPlaying ? (
                      <svg viewBox="0 0 24 24" width="32" height="32">
                        <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="32" height="32">
                        <path fill="currentColor" d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </div>
                </button>
              </div>
              {isPlaying && (
                <div className="latest-release__audio-viz">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="latest-release__viz-bar"></div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div 
            className="latest-release__info"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="latest-release__meta">
              <span className="latest-release__type">{latestRelease.type}</span>
              <span className="latest-release__date">
                {new Date(latestRelease.releaseDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </span>
            </div>

            <h3 className="latest-release__track-title">{latestRelease.title}</h3>
            <p className="latest-release__artist">by {latestRelease.artist}</p>

            <div className="latest-release__details">
              <div className="latest-release__detail">
                <span className="latest-release__detail-label">Duration:</span>
                <span className="latest-release__detail-value">{latestRelease.duration}</span>
              </div>
              <div className="latest-release__detail">
                <span className="latest-release__detail-label">Genre:</span>
                <span className="latest-release__detail-value">{latestRelease.genre}</span>
              </div>
            </div>

            <p className="latest-release__description">
              {latestRelease.description}
            </p>

            <div className="latest-release__streaming">
              <h4 className="latest-release__streaming-title">Listen Now</h4>
              <div className="latest-release__streaming-links">
                {latestRelease.streamingLinks.map((link) => (
                  <a
                    key={link.platform}
                    href={link.url}
                    className="latest-release__streaming-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="latest-release__streaming-icon">
                      {link.platform.charAt(0).toUpperCase()}
                    </span>
                    <span className="latest-release__streaming-text">
                      {link.platform}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="latest-release__system-info">
              <div className="latest-release__system-status">
                <span>TRACK_ID:</span>
                <span>{latestRelease.id.toString().padStart(4, '0')}</span>
              </div>
              <div className="latest-release__system-status">
                <span>STATUS:</span>
                <span className="latest-release__system-active">ACTIVE</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default LatestRelease;